import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";

export enum WebScraperJobStatus {
  NOT_STARTED = "NOT_STARTED",
  RUNNING = "RUNNING",
  PAUSED = "PAUSED",
  DONE = "DONE",
  FAILED = "FAILED",
}

export enum WebScraperItemStatus {
  DISCOVERED = "DISCOVERED",
  IMPORTING = "IMPORTING",
  IMPORTED = "IMPORTED",
  SKIPPED = "SKIPPED",
  FAILED = "FAILED",
}

export enum WebScraperItemType {
  FILE = "FILE",
  PAGE = "PAGE",
  IMAGE = "IMAGE",
}

export enum WebScraperFileType {
  PDF = "PDF",
  DOCX = "DOCX",
  DOC = "DOC",
  XLSX = "XLSX",
  XLS = "XLS",
  PPTX = "PPTX",
  PPT = "PPT",
  CSV = "CSV",
  IMAGE = "IMAGE",
  ZIP = "ZIP",
  RAR = "RAR",
  OTHER = "OTHER",
}

export interface WebScrapeJob {
  _id: string;
  tenant_key: string;
  created_by: string;
  document_store_id: string;
  seed_url: string;
  allowed_domain: string;
  max_depth: number;
  max_pages: number;
  max_file_size_bytes: number;
  concurrency: number;
  request_delay_ms: number;
  allowed_file_types: WebScraperFileType[];
  use_playwright_fallback: boolean;
  respect_robots_txt: boolean;
  crawl_subdomains: boolean;
  custom_headers: Record<string, string>;
  local_storage: Record<string, string>;
  login_config?: WebScraperLoginConfig;
  status: WebScraperJobStatus;
  abort_requested: boolean;
  started_at: string | null;
  completed_at: string | null;
  last_run_at: string | null;
  error_message: string | null;
  current_url: string | null;
  pages_crawled: number;
  files_discovered: number;
  files_cached: number;
  files_failed: number;
  items_imported: number;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebScrapeDiscoveredItem {
  _id: string;
  job_id: string;
  tenant_key: string;
  source_url: string;
  found_on_page_url: string;
  depth: number;
  item_type: WebScraperItemType;
  file_name: string | null;
  file_type: WebScraperFileType | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  page_title: string | null;
  page_text_snippet: string | null;
  page_full_text: string | null;
  meta_description: string | null;
  meta_og_title: string | null;
  meta_og_image: string | null;
  status: WebScraperItemStatus;
  error_message: string | null;
  uploaded_file_id: string | null;
  document_store_id: string | null;
  imported_at: string | null;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebScraperLoginConfig {
  login_url: string;
  username_selector: string;
  password_selector: string;
  submit_selector: string;
  username: string;
  password: string;
  success_url_pattern?: string;
  wait_for_selector?: string;
}

export interface CreateJobDto {
  document_store_id: string;
  seed_url: string;
  max_depth?: number;
  max_pages?: number;
  max_file_size_bytes?: number;
  concurrency?: number;
  request_delay_ms?: number;
  allowed_file_types?: WebScraperFileType[];
  use_playwright_fallback?: boolean;
  respect_robots_txt?: boolean;
  crawl_subdomains?: boolean;
  custom_headers?: Record<string, string>;
  local_storage?: Record<string, string>;
  login_config?: WebScraperLoginConfig;
}

export interface ListJobsParams {
  document_store_id?: string;
  status?: WebScraperJobStatus;
  search?: string;
  is_deleted?: boolean;
  skip?: number;
  limit?: number;
}

export interface ListItemsParams {
  item_type?: WebScraperItemType;
  status?: WebScraperItemStatus;
  file_type?: WebScraperFileType;
  search?: string;
  skip?: number;
  limit?: number;
}

export interface ImportItemsDto {
  item_ids?: string[];
  import_all?: boolean;
}

export interface ImportResult {
  imported: number;
  failed: number;
}

export type UndoSkipResult =
  | { action: "reset_to_discovered"; count: number }
  | { action: "imported"; imported: number; failed: number };

export enum WebScraperSkipReason {
  ROBOTS_TXT_DISALLOWED = "ROBOTS_TXT_DISALLOWED",
  MAX_DEPTH_EXCEEDED    = "MAX_DEPTH_EXCEEDED",
  FILE_TYPE_NOT_ALLOWED = "FILE_TYPE_NOT_ALLOWED",
  FILE_TOO_LARGE        = "FILE_TOO_LARGE",
  DOMAIN_NOT_ALLOWED    = "DOMAIN_NOT_ALLOWED",
  HEAD_REQUEST_FAILED   = "HEAD_REQUEST_FAILED",
  UNSUPPORTED_PROTOCOL  = "UNSUPPORTED_PROTOCOL",
  UNKNOWN_CONTENT_TYPE  = "UNKNOWN_CONTENT_TYPE",
}

export interface WebScraperSkippedUrl {
  _id: string;
  job_id: string;
  url: string;
  found_on_page_url: string;
  depth: number;
  reason: WebScraperSkipReason;
  detail: string;
  createdAt: string;
}

export interface ListSkippedParams {
  reason?: WebScraperSkipReason;
  search?: string;
  skip?: number;
  limit?: number;
}

export interface ListSkippedResult {
  total: number;
  skip: number;
  limit: number;
  list: WebScraperSkippedUrl[];
  summary: Partial<Record<WebScraperSkipReason, number>>;
}

export interface PreviewUrlDto {
  url: string;
  use_playwright_fallback?: boolean;
  custom_headers?: Record<string, string>;
  local_storage?: Record<string, string>;
  login_config?: WebScraperLoginConfig;
}

export interface PreviewFileLink {
  url: string;
  fileName: string;
  fileType: WebScraperFileType;
}

export interface PreviewUrlResult {
  url: string;
  page_title: string;
  meta_description: string;
  fetch_time_ms: number;
  file_link_count: number;
  page_link_count: number;
  external_link_count: number;
  file_links: PreviewFileLink[];
  page_links: string[];
  capped: boolean;
}

class WebScraperService {
  async listJobs(params: ListJobsParams): Promise<{ list: WebScrapeJob[]; total: number }> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.LIST, params);
    return response.data.data;
  }

  async createJob(dto: CreateJobDto): Promise<WebScrapeJob> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.CREATE, dto);
    return response.data.data;
  }

  async getJobById(id: string): Promise<WebScrapeJob> {
    const response = await apiClient.get(API_ENDPOINTS.WEB_SCRAPER.GET_BY_ID(id));
    return response.data.data;
  }

  async startJob(id: string): Promise<WebScrapeJob> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.START(id));
    return response.data.data;
  }

  async pauseJob(id: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.PAUSE(id));
  }

  async resumeJob(id: string): Promise<WebScrapeJob> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.RESUME(id));
    return response.data.data;
  }

  async cancelJob(id: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.CANCEL(id));
  }

  async deleteJob(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.WEB_SCRAPER.DELETE(id));
  }

  async listItems(
    jobId: string,
    params: ListItemsParams
  ): Promise<{ list: WebScrapeDiscoveredItem[]; total: number }> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.ITEMS_LIST(jobId), params);
    return response.data.data;
  }

  async skipItems(jobId: string, itemIds: string[]): Promise<void> {
    await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.ITEMS_SKIP(jobId), { item_ids: itemIds });
  }

  async undoSkipItems(jobId: string, itemIds: string[]): Promise<UndoSkipResult> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.ITEMS_UNDO_SKIP(jobId), { item_ids: itemIds });
    return response.data.data;
  }

  async importItems(jobId: string, dto: ImportItemsDto): Promise<ImportResult> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.IMPORT(jobId), dto);
    return response.data.data;
  }

  async listSkippedUrls(jobId: string, params: ListSkippedParams = {}): Promise<ListSkippedResult> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.SKIPPED_LIST(jobId), params);
    return response.data.data;
  }

  async previewUrl(dto: PreviewUrlDto): Promise<PreviewUrlResult> {
    const response = await apiClient.post(API_ENDPOINTS.WEB_SCRAPER.PREVIEW, dto);
    return response.data.data;
  }
}

const webScraperService = new WebScraperService();
export default webScraperService;
